import React, { useEffect } from "react";

const InjectScript = React.memo(({ script, type }) => {
  const divRef = React.useRef(null);

  useEffect(() => {
    if (!divRef.current || !script) return;

    const doc = document.createRange().createContextualFragment(script);

    // Fix accessibility for CMS-provided iframes missing titles
    const iframes = Array.from(doc.querySelectorAll("iframe:not([title])"));
    iframes.forEach((iframe) => {
      const src = iframe.src || "";
      if (src.includes("youtube.com")) iframe.title = "YouTube Video Embed";
      else if (src.includes("instagram.com")) iframe.title = "Instagram Post Embed";
      else if (src.includes("twitter.com")) iframe.title = "Twitter Post Embed";
      else iframe.title = "Embedded Content";
    });

    // Fix accessibility for empty links in CMS content (Instagram/Twitter/Social blocks)
    const embedLinks = Array.from(doc.querySelectorAll("blockquote.instagram-media a:not([aria-label]), blockquote.twitter-tweet a:not([aria-label]), .allSubscribe a:not([aria-label])"));
    embedLinks.forEach((link) => {
      if (!link.textContent.trim()) {
        let label = "Embedded post link";
        const cls = link.className || "";
        if (cls.includes("whatsapp")) label = "WhatsApp";
        else if (cls.includes("facebook")) label = "Facebook";
        else if (cls.includes("twitter")) label = "Twitter";
        else if (cls.includes("instagram")) label = "Instagram";
        else if (cls.includes("yt") || cls.includes("youtube")) label = "YouTube";
        
        link.setAttribute("aria-label", label);
      }
    });

    // Extract all script tags to defer their execution
    const scripts = Array.from(doc.querySelectorAll("script"));
    const scriptData = scripts.map((s) => {
      const attributes = Array.from(s.attributes).map(attr => ({ name: attr.name, value: attr.value }));
      const textContent = s.textContent;
      s.remove(); // Remove from fragment so it doesn't execute immediately on initial append
      return { attributes, textContent };
    });

    // Clear existing content and append script fragment atomically (now without blocking scripts)
    divRef.current.textContent = ""; 
    divRef.current.appendChild(doc);

    // Defer script execution until the element is near the viewport
    let observer;
    if (scriptData.length > 0) {
      observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries[0].isIntersecting) {
            scriptData.forEach((data) => {
              const newScript = document.createElement("script");
              data.attributes.forEach(attr => newScript.setAttribute(attr.name, attr.value));
              newScript.textContent = data.textContent;
              if (divRef.current) {
                divRef.current.appendChild(newScript);
              }
            });
            obs.disconnect();
          }
        },
        { rootMargin: "400px" } // Load slightly before it comes into view
      );
      observer.observe(divRef.current);
    }

    if (type === "taboola") {
      setTimeout(() => {
        if (divRef.current && doc) {
          divRef.current.appendChild(doc);
        }
      }, 1000);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [script, type]);

  return <div ref={divRef} />;
});

export default InjectScript;
